import { http } from '@/http/axios';
import type {
  AnalyticsGraphData,
  AnalyticsStats,
  AttendeeEngagementDto,
  GeographicDistribution,
  ParticipantFullDetails,
  PostLiveFeedbackEligibility,
  PostLiveFeedbackInput,
  SessionAnalyticsDetails,
  SessionComparisonDetails,
  TechnicalDistribution,
  WebinarStatsRow,
} from '../types';

// L'export prospects d'une grosse session peut dépasser le timeout axios par défaut (20s) :
// on reste au-dessus du timeout async du back (90s) pour recevoir sa réponse plutôt que d'avorter.
export const PROSPECTS_EXPORT_REQUEST_TIMEOUT_MS = 95000;

export interface PageResponse<T> {
  content: T[];
  totalPages: number;
  totalElements: number;
  size: number;
  number: number;
}

export const analyticsService = {
  getSummary: async (
    workspaceIdentifier: number,
    startDateIso?: string,
    endDateIso?: string,
  ): Promise<AnalyticsStats> => {
    const response = await http.get<AnalyticsStats>(
      `/workspaces/${workspaceIdentifier}/analytics/summary`,
      {
        params: { from: startDateIso, to: endDateIso },
      },
    );
    return response.data;
  },

  getRegistrationsGraph: async (
    workspaceIdentifier: number,
    startDateIso?: string,
    endDateIso?: string,
  ): Promise<AnalyticsGraphData> => {
    const response = await http.get<AnalyticsGraphData>(
      `/workspaces/${workspaceIdentifier}/analytics/graphs/registrations`,
      {
        params: { from: startDateIso, to: endDateIso },
      },
    );
    return response.data;
  },

  getOfferClicksGraph: async (
    workspaceIdentifier: number,
    startDateIso?: string,
    endDateIso?: string,
  ): Promise<AnalyticsGraphData> => {
    const response = await http.get<AnalyticsGraphData>(
      `/workspaces/${workspaceIdentifier}/analytics/graphs/offer-clicks`,
      {
        params: { from: startDateIso, to: endDateIso },
      },
    );
    return response.data;
  },

  getGeographicDistribution: async (
    workspaceIdentifier: number,
    startDateIso?: string,
    endDateIso?: string,
  ): Promise<GeographicDistribution> => {
    const response = await http.get<GeographicDistribution>(
      `/workspaces/${workspaceIdentifier}/analytics/geographic-distribution`,
      {
        params: { from: startDateIso, to: endDateIso },
      },
    );
    return response.data;
  },

  getTechnicalDistribution: async (
    workspaceIdentifier: number,
    startDateIso?: string,
    endDateIso?: string,
  ): Promise<TechnicalDistribution> => {
    const response = await http.get<TechnicalDistribution>(
      `/workspaces/${workspaceIdentifier}/analytics/technical-distribution`,
      {
        params: { from: startDateIso, to: endDateIso },
      },
    );
    return response.data;
  },

  getPastWebinars: async (
    workspaceIdentifier: number,
    pageIndex: number = 0,
    pageSize: number = 20,
  ): Promise<PageResponse<WebinarStatsRow>> => {
    const response = await http.get<PageResponse<WebinarStatsRow>>(
      `/workspaces/${workspaceIdentifier}/analytics/past-webinars`,
      {
        params: {
          page: pageIndex,
          size: pageSize,
        },
      },
    );
    return response.data;
  },

  getSessionDetails: async (
    workspaceIdentifier: number,
    sessionIdentifier: number,
  ): Promise<SessionAnalyticsDetails> => {
    const response = await http.get<SessionAnalyticsDetails>(
      `/workspaces/${workspaceIdentifier}/analytics/sessions/${sessionIdentifier}/details`,
    );
    return response.data;
  },

  getSessionParticipants: async (
    workspaceIdentifier: number,
    sessionIdentifier: number,
    pageIndex: number = 0,
    pageSize: number = 50,
  ): Promise<PageResponse<AttendeeEngagementDto>> => {
    const response = await http.get<PageResponse<AttendeeEngagementDto>>(
      `/workspaces/${workspaceIdentifier}/analytics/sessions/${sessionIdentifier}/participants`,
      {
        params: {
          page: pageIndex,
          size: pageSize,
        },
      },
    );
    return response.data;
  },

  getPostLiveFeedbackEligibility: async (
    workspaceIdentifier: number,
    sessionIdentifier: number,
  ): Promise<PostLiveFeedbackEligibility> => {
    const response = await http.get<PostLiveFeedbackEligibility>(
      `/workspaces/${workspaceIdentifier}/analytics/sessions/${sessionIdentifier}/post-live-feedback/eligibility`,
    );
    return response.data;
  },

  submitPostLiveFeedback: async (
    workspaceIdentifier: number,
    sessionIdentifier: number,
    input: PostLiveFeedbackInput,
  ): Promise<void> => {
    await http.post(
      `/workspaces/${workspaceIdentifier}/analytics/sessions/${sessionIdentifier}/post-live-feedback`,
      input,
    );
  },

  getParticipantFullDetails: async (
    workspaceIdentifier: number,
    sessionIdentifier: number,
    attendeeIdentifier: number,
  ): Promise<ParticipantFullDetails> => {
    const response = await http.get<ParticipantFullDetails>(
      `/workspaces/${workspaceIdentifier}/analytics/sessions/${sessionIdentifier}/attendees/${attendeeIdentifier}/full-details`,
    );
    return response.data;
  },

  getFullComparison: async (
    workspaceIdentifier: number,
    sessionIdA: number,
    sessionIdB: number,
  ): Promise<SessionComparisonDetails> => {
    const response = await http.get<SessionComparisonDetails>(
      `/workspaces/${workspaceIdentifier}/analytics/compare-full`,
      {
        params: {
          sessionIdA,
          sessionIdB,
        },
      },
    );
    return response.data;
  },

  getReplayDownloadUrl: (
    workspaceIdentifier: number,
    sessionIdentifier: number,
    type: 'desktop' | 'mobile',
  ): string => {
    const baseUrl = (import.meta.env.VITE_API_URL as string)?.replace(/\/$/, '');
    return `${baseUrl}/workspaces/${workspaceIdentifier}/analytics/sessions/${sessionIdentifier}/replays/${type}/download`;
  },

  exportSessionProspects: (workspaceIdentifier: number, sessionIdentifier: number): Promise<Blob> =>
    http
      .get<Blob>(
        `/workspaces/${workspaceIdentifier}/analytics/sessions/${sessionIdentifier}/export-prospects`,
        { responseType: 'blob', timeout: PROSPECTS_EXPORT_REQUEST_TIMEOUT_MS },
      )
      .then((r) => r.data),

  exportSessionChatComplete: (
    workspaceIdentifier: number,
    sessionIdentifier: number,
  ): Promise<Blob> =>
    http
      .get<Blob>(
        `/workspaces/${workspaceIdentifier}/analytics/sessions/${sessionIdentifier}/export-chat-complete`,
        { responseType: 'blob' },
      )
      .then((r) => r.data),
};
